import { $menuExpandedItemIds, $menuState, initialMenuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { isProcessMenuItemType, MenuItem, MenuType } from '@nx-frontend/shared/types';
import { MenusState } from '@nx-frontend/shared/types';

export function rootOrderIdsParams(menuType: MenuType) {
  return {
    keys: [menuType],
    defaultValue: [],
    store: $menuState,
    fn: (state: MenusState, [type]: MenuType[]) => state[type]?.rootOrder,
  };
}

export function getMenuItemsLengthParams(menuType: MenuType) {
  return {
    keys: [menuType],
    defaultValue: 0,
    store: $menuState,
    fn: (state: MenusState, [type]: MenuType[]) => {
      const menu = state[type];
      if (menu) {
        const ids = menu?.ids;
        const entities = menu.entities;
        return ids?.reduce((acc, id) => {
          const item = entities[id];
          if (item && !item.deleted) {
            acc++;
          }
          return acc;
        }, 0);
      }
      return 0;
    },
    updateFilter: (next: number, prev: number) => {
      return next !== prev;
    },
  };
}

export function getMenuItemParams(id: string, menuType: MenuType) {
  return {
    keys: [id],
    defaultValue: undefined,
    store: $menuState,
    fn: (state: MenusState, [keyId]: string[]) => (state[menuType] ?? initialMenuState).entities[keyId],
    updateFilter: (next: MenuItem, prev: MenuItem) => {
      return (
        next?.name !== prev?.name ||
        next?.visible !== prev?.visible ||
        next?.url !== prev?.url ||
        next?.order?.toString() !== prev?.order?.toString() ||
        next?.iconUrl !== prev?.iconUrl ||
        next?.parentId !== prev?.parentId ||
        next?.permissions?.allowAll !== prev?.permissions?.allowAll ||
        next?.permissions?.allowUserIds.length !== prev?.permissions?.allowUserIds.length ||
        next?.icon !== prev?.icon ||
        (isProcessMenuItemType(next) && isProcessMenuItemType(prev) && next?.process !== prev?.process)
      );
    },
  };
}

export function getMenuItemExpanded(id: string) {
  return {
    keys: [id],
    defaultValue: undefined,
    store: $menuExpandedItemIds,
    fn: (state: string[], [keyId]: string[]) => state.includes(keyId),
    updateFilter: (next: boolean, prev: boolean) => next !== prev,
  };
}

export function getIsMenuItemActiveParams(id: string, menuType: MenuType) {
  return {
    keys: [id],
    defaultValue: false,
    store: $menuState,
    fn: (state: MenusState, [id]: string[]) => (state[menuType] ?? initialMenuState).activeItemIds.includes(id),
    updateFilter: (next: boolean, prev: boolean) => {
      return next !== prev;
    },
  };
}

export function getMenuActiveItemIdsParams(menuType: MenuType, verifyLength = true) {
  return {
    keys: [menuType],
    defaultValue: false,
    store: $menuState,
    fn: (state: MenusState, [menuType]: MenuType[]) => (state[menuType] ?? initialMenuState).activeItemIds,

    ...(verifyLength
      ? {
          updateFilter: (next: string[], prev: string[]) => {
            return next.length !== prev.length;
          },
        }
      : {}),
  };
}

export function getSelectedMenuItems(menuType: MenuType) {
  return {
    keys: [menuType],
    defaultValue: [],
    store: $menuState,
    fn: (state: MenusState, [type]: MenuType[]) => {
      const menu = state[type];
      const entities = menu.entities;
      const activeItemIds = menu.activeItemIds;

      if (entities && activeItemIds) {
        return activeItemIds.reduce((acc, id) => {
          const item = entities[id];
          if (item && item.type) {
            acc.push(item);
          }
          return acc;
        }, [] as MenuItem[]);
      }
      return [];
    },
  };
}

export function getSidebarRootMenuItems(collapsed: boolean, path: string, collapsedItemsLimit: number) {
  return {
    keys: [MenuType.PORTAL_SIDEBAR, collapsed, path, collapsedItemsLimit] as [MenuType, boolean, string, number],
    defaultValue: [],
    store: $menuState,
    fn: (state: MenusState, [type, collapsed, path, collapsedItemsLimit]: [MenuType, boolean, string, number]) => {
      const menu = state[type];
      const entities = menu.entities;
      if (!menu || !entities) {
        return [];
      }
      const items: MenuItem[] = menu.rootOrder
        .map((id: string) => entities[id])
        .filter((item: MenuItem) => item && item.visible && item.url && !item.deleted);
      if (!collapsed) {
        return items;
      } else {
        const active = items.find((item) => '/' + path.split('/')[1] === item.url);
        const collapsedItems = items.slice(0, collapsedItemsLimit);
        if (active) {
          if (collapsedItems.some((item) => item.id === active.id)) {
            return collapsedItems;
          } else {
            return [...collapsedItems.slice(0, collapsedItemsLimit - 2), active];
          }
        }
        return collapsedItems;
      }
    },
  };
}

export function getSidebarSubItems(id: string) {
  return {
    keys: [MenuType.PORTAL_SIDEBAR, id] as [MenuType, string],
    defaultValue: [],
    store: $menuState,
    fn: (state: MenusState, [type, id]: [MenuType, string]) => {
      const menu = state[type];
      const entities = menu.entities;
      if (!menu || !entities) {
        return [];
      }
      const item = entities[id];
      return item.order
        ?.map((orderId) => {
          return entities[orderId];
        })
        .filter((item) => item && item.visible);
    },
  };
}

export function getIsMenuPermissionsTouchedParams(menuType: MenuType) {
  return {
    keys: [menuType],
    defaultValue: [],
    store: $menuState,
    fn: (state: MenusState, [type]: MenuType[]) =>
      Object.values(state[type]?.entities).some((item) => !item.permissions?.allowAll),
    updateFilter: (next: boolean, prev: boolean) => next !== prev,
  };
}
